import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { User } from 'oidc-client-ts';
import { getOidcUserManager } from '../../services/oidcClient';
import { apiClient } from '../../services/apiClient';

export interface AdminProfile {
  id: string;
  email: string;
  fullName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  mfaRequired: boolean;
  mfaEnrollmentRequired: boolean;
}

interface AuthContextType {
  user: User | null;
  admin: AdminProfile | null;
  loading: boolean;
  /**
   * Authentication Context Class — Zitadel maps the MFA assurance level to the
   * standard <c>acr</c> claim. We read it from the access token's claims via
   * UserManager so existing UI logic that gates on "is the admin MFA-verified"
   * keeps working without depending on Zitadel SDKs.
   *
   * Returns "aal2" when Zitadel reports an MFA-verified session, otherwise "aal1".
   */
  aalLevel: string;
  signIn: (from?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAdmin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function deriveAalLevel(user: User | null): string {
  if (!user) return 'aal1';
  // Zitadel emits acr / amr; both can indicate a multi-factor login.
  const acr = (user.profile.acr as string | undefined) ?? '';
  const amr = (user.profile.amr as string[] | undefined) ?? [];
  if (acr.includes('mfa') || acr.includes('aal2')) return 'aal2';
  if (amr.some((m) => m === 'mfa' || m === 'otp' || m === 'totp')) return 'aal2';
  return 'aal1';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [aalLevel, setAalLevel] = useState<string>('aal1');
  const syncingRef = useRef(false);

  const syncAdminProfile = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    try {
      const profile = await apiClient.post<AdminProfile>('/admin/auth/sign-in');
      setAdmin(profile);
    } catch (err) {
      console.warn('[AdminAuth] failed to load admin profile — signing out', err);
      setAdmin(null);
      // Remove the OIDC user so AuthGuard redirects to /login instead of
      // spinning indefinitely on the "Loading profile…" screen.
      const mgr = await getOidcUserManager();
      await mgr.removeUser();
      setUser(null);
    } finally {
      syncingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    apiClient.setTokenProvider(async () => {
      const mgr = await getOidcUserManager();
      const u = await mgr.getUser();
      return u && !u.expired ? u.access_token : null;
    });

    getOidcUserManager()
      .then((mgr) => {
        if (cancelled) return;

        mgr.getUser().then((u) => {
          if (cancelled) return;
          setUser(u);
          setAalLevel(deriveAalLevel(u));
          if (u && !u.expired) {
            syncAdminProfile().finally(() => setLoading(false));
          } else {
            setLoading(false);
          }
        });

        const onUserLoaded = (u: User) => {
          setUser(u);
          setAalLevel(deriveAalLevel(u));
          syncAdminProfile();
        };
        const onUserUnloaded = () => {
          setUser(null);
          setAdmin(null);
          setAalLevel('aal1');
        };
        const onAccessTokenExpired = () => {
          // Silent renew is on; if it fails, user.expired flips to true and the
          // next API call returns null token. No state mutation needed here.
        };
        const onSilentRenewError = (e: Error) => {
          console.warn('[AdminAuth] silent renew failed', e);
        };

        mgr.events.addUserLoaded(onUserLoaded);
        mgr.events.addUserUnloaded(onUserUnloaded);
        mgr.events.addAccessTokenExpired(onAccessTokenExpired);
        mgr.events.addSilentRenewError(onSilentRenewError);

        cleanup = () => {
          mgr.events.removeUserLoaded(onUserLoaded);
          mgr.events.removeUserUnloaded(onUserUnloaded);
          mgr.events.removeAccessTokenExpired(onAccessTokenExpired);
          mgr.events.removeSilentRenewError(onSilentRenewError);
        };
      })
      .catch((err) => {
        console.error('[AdminAuth] OIDC bootstrap failed', err);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [syncAdminProfile]);

  const signIn = async (from?: string) => {
    const mgr = await getOidcUserManager();
    await mgr.signinRedirect(from ? { state: { from } } : undefined);
  };

  const signOut = async () => {
    const mgr = await getOidcUserManager();
    await mgr.signoutRedirect();
    setUser(null);
    setAdmin(null);
  };

  const refreshAdmin = async () => {
    await syncAdminProfile();
  };

  return (
    <AuthContext.Provider value={{ user, admin, loading, aalLevel, signIn, signOut, refreshAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
